import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAdminTrials, useAdminCheckins, adminApi, useAdminMutation, errorMessage, fmt, inputCls } from "@/hooks/useAdmin";
import { AdminHeading, Badge } from "@/components/admin/AdminLayout";

const STATUSES = ["awaiting_dispatch", "dispatched", "in_progress", "completed", "withdrawn"];
const tone = { awaiting_dispatch: "champagne", dispatched: "sage", in_progress: "forest", completed: "sage", withdrawn: "muted" };

function DispatchForm({ trial }) {
  const [f, setF] = useState({ carrier: "", tracking_number: "" });
  const dispatch = useAdminMutation(adminApi.dispatch, () => toast.success("Dispatch recorded. The member can now see tracking."));
  return (
    <form onSubmit={(e) => { e.preventDefault(); dispatch.mutate({ id: trial.trial_id, ...f }, { onError: (err) => toast.error(errorMessage(err)) }); }} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mt-4" data-testid={`dispatch-form-${trial.trial_id}`}>
      <input placeholder="Carrier" value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} className={inputCls} data-testid={`dispatch-carrier-${trial.trial_id}`} />
      <input placeholder="Tracking number" value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} className={inputCls} data-testid={`dispatch-tracking-${trial.trial_id}`} />
      <button type="submit" disabled={dispatch.isPending} className="btn btn-primary !px-4 !py-2.5 disabled:opacity-60" data-testid={`dispatch-submit-${trial.trial_id}`}>Mark dispatched</button>
    </form>
  );
}

function Checkins({ trialId }) {
  const { data: checkins = [] } = useAdminCheckins(trialId);
  if (checkins.length === 0) return <p className="mt-3 text-sm text-charcoal/50 font-light">No check-ins recorded yet.</p>;
  return (
    <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid={`admin-checkins-${trialId}`}>
      {checkins.map((c) => (
        <div key={c.day} className="border border-forest/10 p-4 text-sm">
          <div className="flex justify-between"><p className="font-serif text-lg text-forest">Day {c.day}</p><Badge tone={c.status === "saved" ? "sage" : "muted"}>{c.status}</Badge></div>
          <dl className="mt-2 space-y-1 text-charcoal/75 font-light">
            {c.texture && <div><dt className="inline text-charcoal/45">Texture: </dt><dd className="inline">{c.texture}</dd></div>}
            <div><dt className="inline text-charcoal/45">Comfort / fit / overall: </dt><dd className="inline">{c.comfort ?? "—"} / {c.routine_fit ?? "—"} / {c.overall ?? "—"}</dd></div>
            <div><dt className="inline text-charcoal/45">Would continue: </dt><dd className="inline">{c.would_continue === true ? "Yes" : c.would_continue === false ? "No" : "Not sure"}</dd></div>
            {c.notes && <div><dt className="inline text-charcoal/45">Notes: </dt><dd className="inline italic">{c.notes}</dd></div>}
          </dl>
        </div>
      ))}
    </div>
  );
}

function TrialRow({ t }) {
  const [open, setOpen] = useState(false);
  const m = t.member || {};
  return (
    <div className="border border-forest/15 bg-cream p-5" data-testid={`admin-trial-${t.trial_id}`} data-status={t.status}>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-center">
        <div><p className="font-serif text-xl text-forest">{m.display_name || m.name || "Member"}</p><p className="text-xs text-charcoal/55">{m.email}</p></div>
        <div><p className="text-sm text-charcoal/75">{t.programme_title} · {t.product_name}</p><p className="text-xs text-charcoal/45">Accepted {fmt(t.created_at)}{t.tracking?.tracking_number && ` · ${t.tracking.carrier || ""} ${t.tracking.tracking_number}`}</p></div>
        <Badge tone={tone[t.status]}>{t.status.replace("_", " ")}</Badge>
      </div>
      {t.status === "awaiting_dispatch" && (
        <>
          {t.shipping && <p className="mt-4 text-sm text-charcoal/75"><span className="text-charcoal/45">Ship to: </span>{[t.shipping.full_name, t.shipping.line1, t.shipping.line2, t.shipping.city, t.shipping.postcode, t.shipping.country].filter(Boolean).join(", ")}{t.shipping.phone && ` · ${t.shipping.phone}`}</p>}
          <DispatchForm trial={t} />
        </>
      )}
      {["in_progress", "completed"].includes(t.status) && (
        <div className="mt-4">
          <button onClick={() => setOpen((o) => !o)} className="text-[11px] uppercase tracking-[0.2em] text-forest link-underline" data-testid={`view-checkins-${t.trial_id}`}>{open ? "Hide" : "View"} private check-ins ({t.checkins_count} saved)</button>
          {open && <Checkins trialId={t.trial_id} />}
        </div>
      )}
    </div>
  );
}

export default function AdminDispatch() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "awaiting_dispatch";
  const { data: trials = [] } = useAdminTrials(status === "all" ? "" : status);
  return (
    <div data-testid="admin-dispatch">
      <AdminHeading eyebrow="Fulfilment & follow-up" title="Dispatch & trials" />
      <div className="flex flex-wrap gap-2 mb-6">
        {["awaiting_dispatch", ...STATUSES.slice(1), "all"].map((s) => (
          <button key={s} onClick={() => setParams({ status: s })} className={`px-3.5 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${status === s ? "bg-forest text-ivory border-forest" : "border-forest/20 text-charcoal/60 hover:border-forest"}`} data-testid={`trial-filter-${s}`}>{s.replace("_", " ")}</button>
        ))}
      </div>
      <div className="space-y-3">
        {trials.length === 0 && <p className="text-sm text-charcoal/50 font-light border border-forest/10 p-8 text-center" data-testid="trials-empty">No trials in this state.</p>}
        {trials.map((t) => <TrialRow key={t.trial_id} t={t} />)}
      </div>
      <p className="mt-8 text-xs text-charcoal/45 font-light">Check-ins are private to the member and ELA. They are never published; public stories are moderated separately.</p>
    </div>
  );
}
